const potentialKey = 'Bronpotentieel_jaar_bronsleutel6';
const heatDeliveredKey = 'totale_warmte_geleverd_combosleutel_bs6_vs1';
const deltaTKey = 'DeltaTavg_bronsleutel6';

function getSegmentProperties(segment) {
  if (!segment) return {};
  if (segment.properties) return segment.properties;

  return Object.fromEntries(
    Object.entries(segment).filter(([key]) => key !== 'geometry'),
  );
}

function getSegmentId(segment) {
  const properties = getSegmentProperties(segment);
  const id = segment?.id ?? properties.ID_100m;

  return id === undefined || id === null ? undefined : String(id);
}

function toIdArray(value) {
  if (Array.isArray(value)) return value.map(String);
  if (value === undefined || value === null || value === '') return [];

  return [String(value)];
}

function getMunicipalityId(sectorCode) {
  if (sectorCode === undefined || sectorCode === null) return undefined;

  const code = String(sectorCode).trim();
  if (code.length < 5) return undefined;

  return code.slice(0, 5);
}

/**
 * Orders the 100m pieces from source (upstream) to mouth (downstream) using the
 * ID_stroomopwaarts (upstream) and ID_stroomafwaarts (downstream) attributes.
 */
function orderFlowingWaterSegments(segments) {
  const pieces = (segments || []).filter(Boolean);
  if (pieces.length === 0) return [];

  const byId = new Map();
  pieces.forEach((piece) => {
    const id = getSegmentId(piece);
    if (id !== undefined) byId.set(id, piece);
  });

  const followChain = (start, visited) => {
    const chain = [];
    let current = start;

    while (current) {
      const id = getSegmentId(current);
      if (id === undefined || visited.has(id)) break;

      visited.add(id);
      chain.push(current);

      const properties = getSegmentProperties(current);
      const next = toIdArray(properties.ID_stroomafwaarts)
        .map(downstreamId => byId.get(downstreamId))
        .find(candidate => candidate && !visited.has(getSegmentId(candidate)));
      current = next;
    }

    return chain;
  };

  const isHead = (piece) => {
    const properties = getSegmentProperties(piece);
    const upstreamIds = toIdArray(properties.ID_stroomopwaarts);

    return upstreamIds.every(upstreamId => !byId.has(upstreamId));
  };

  const heads = pieces.filter(isHead);
  const visited = new Set();
  const ordered = [];

  heads.forEach((head) => {
    ordered.push(...followChain(head, visited));
  });

  // Append any pieces that were not reachable from a detected head.
  pieces.forEach((piece) => {
    const id = getSegmentId(piece);
    if (id !== undefined && !visited.has(id)) {
      ordered.push(...followChain(piece, visited));
    }
  });

  return ordered;
}

function toNumber(value) {
  if (value === undefined || value === null || value === '') return undefined;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

/**
 * Builds the chart points for the ordered pieces. Potential and heat delivered
 * are converted to MW (value / 1e6) to match the left axis, deltaT stays in °C.
 */
function getPotentialGraphPoints(orderedSegments) {
  return orderedSegments.map((segment, index) => {
    const properties = getSegmentProperties(segment);
    const potential = toNumber(properties[potentialKey]);
    const heatDelivered = toNumber(properties[heatDeliveredKey]);
    const deltaT = toNumber(properties[deltaTKey]);

    return {
      index,
      id: getSegmentId(segment),
      potential: potential === undefined ? undefined : potential / 1_000_000,
      heatDelivered: heatDelivered === undefined ? undefined : heatDelivered / 1_000_000,
      deltaT,
      leftMunicipalityId: getMunicipalityId(properties.stat_sector_linkeroever),
      rightMunicipalityId: getMunicipalityId(properties.stat_sector_rechteroever),
    };
  });
}

/**
 * Groups consecutive pieces that share the same municipality into runs so the
 * navigation axis can render one clickable label per municipality stretch.
 */
function getMunicipalityRuns(orderedSegments, bank) {
  const key = bank === 'right' ? 'stat_sector_rechteroever' : 'stat_sector_linkeroever';
  const runs = [];

  orderedSegments.forEach((segment, index) => {
    const municipalityId = getMunicipalityId(getSegmentProperties(segment)[key]);
    const previousRun = runs[runs.length - 1];

    if (previousRun && previousRun.municipalityId === municipalityId) {
      previousRun.endIndex = index;
      previousRun.count += 1;
      return;
    }

    runs.push({
      municipalityId,
      startIndex: index,
      endIndex: index,
      count: 1,
    });
  });

  return runs;
}

function getUniqueMunicipalityIds(orderedSegments) {
  const ids = new Set();

  orderedSegments.forEach((segment) => {
    const properties = getSegmentProperties(segment);
    [
      getMunicipalityId(properties.stat_sector_linkeroever),
      getMunicipalityId(properties.stat_sector_rechteroever),
    ].forEach((id) => {
      if (id) ids.add(id);
    });
  });

  return [...ids];
}

/**
 * Maps each municipality id (first 5 characters of a sector code) to a
 * representative full sector code, so the name can be resolved through the
 * statistical sector when the municipality id itself is not a valid gemeente.
 */
function getMunicipalitySectorSamples(orderedSegments) {
  const samples = new Map();

  orderedSegments.forEach((segment) => {
    const properties = getSegmentProperties(segment);
    [
      properties.stat_sector_linkeroever,
      properties.stat_sector_rechteroever,
    ].forEach((sectorCode) => {
      const municipalityId = getMunicipalityId(sectorCode);
      if (municipalityId && !samples.has(municipalityId)) {
        samples.set(municipalityId, String(sectorCode));
      }
    });
  });

  return [...samples.entries()].map(([municipalityId, sectorCode]) => ({
    municipalityId,
    sectorCode,
  }));
}

function segmentBelongsToMunicipality(segment, municipalityId) {
  if (!municipalityId) return true;

  const properties = getSegmentProperties(segment);

  return [
    getMunicipalityId(properties.stat_sector_linkeroever),
    getMunicipalityId(properties.stat_sector_rechteroever),
  ].includes(municipalityId);
}

export {
  deltaTKey,
  getMunicipalityId,
  getMunicipalityRuns,
  getMunicipalitySectorSamples,
  getPotentialGraphPoints,
  getSegmentId,
  getSegmentProperties,
  getUniqueMunicipalityIds,
  heatDeliveredKey,
  orderFlowingWaterSegments,
  potentialKey,
  segmentBelongsToMunicipality,
};
